import type { GridProps } from '@chakra-ui/react'
import { Card, CardBody, Flex, Grid, Text } from '@chakra-ui/react'
import type { Offer } from 'BrokerTsLib/src/dtos'
import { Risk } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'

import { CompanyLogo } from '../../components/CompanyLogo'
import { SelectedBadge } from '../../components/InsuranceQuoteItem/SelectedBadge'
import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { useQuotes } from '../../context/QuotesContext'
import { getQuoteId } from '../../utils/getQuoteId'
import { scrollToElement } from '../../utils/scrollToElement'
import { Stars } from '../Stars'

export const HighlightedItem = ({ offer }: { offer: Offer; rest?: any }) => {
  const elementId = getQuoteId(offer.offerId)
  const { insuranceParams } = useInsuranceParams()

  const povPart = offer.parts.find(x => x.riskId === Risk.POV)
  const havPart = offer.parts.find(x => x.riskId === Risk.HAV)

  return (
    <Card
      sx={{
        p: { base: 0, md: 4 },
        cursor: 'pointer',
        transition: 'all 0.2s',
        _hover: {
          boxShadow: 'md',
        },
      }}
      onClick={() => {
        scrollToElement({
          elementId,
          offset: 140,
        })
      }}
    >
      <CardBody sx={{ display: 'grid', gap: 1 }}>
        <Flex sx={{ alignItems: 'center', gap: 4, minH: 8 }}>
          <CompanyLogo
            insurer={{ id: offer.insurerId, name: offer.insurerNameFull }}
            width="3.75rem"
            height="2.5rem"
            title={offer.insurerNameFull}
            offerType={offer.type}
          />

          {insuranceParams?.generalParams.selectedOfferId == offer.offerId && (
            <SelectedBadge
              sx={{
                position: 'absolute',
                top: -2,
                left: -2,
                boxShadow: 'md',
                transform: 'rotate(-3deg)',
              }}
              bg="badge-bg"
            />
          )}

          {!!offer.stars && <Stars rating={offer.stars} isDisabled />}
        </Flex>

        {povPart && <Text sx={{ fontSize: 'xs' }}>{povPart.nameNew ?? povPart.name}</Text>}

        {havPart && <Text sx={{ fontSize: 'xs' }}>{havPart.nameNew ?? havPart.name}</Text>}

        {offer.price && (
          <Text sx={{ fontSize: 'sm', fontWeight: 'bold' }}>
            Celkem: {priceFormat(offer.price)}
          </Text>
        )}
      </CardBody>
    </Card>
  )
}

export interface InsuranceQuoteHightligtedProps extends GridProps {
  quotes: Offer[]
}

const sortQuotes = (a: Offer, b: Offer) => {
  if (a.stars && b.stars) {
    return b.stars - a.stars
  }

  return 0
}

export const InsuranceQuoteHightligted = ({
  quotes,
  children,
  sx,
  ...rest
}: InsuranceQuoteHightligtedProps) => {
  const { calculationState } = useQuotes()

  if (!calculationState.isCalculated) return null

  return (
    <Grid
      sx={{
        gridTemplateColumns: {
          base: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: 4,
        ...sx,
      }}
      {...rest}
    >
      {quotes.sort(sortQuotes).map((quote, index) => {
        return <HighlightedItem key={index} offer={quote} />
      })}

      {children}
    </Grid>
  )
}
